import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const AdaptestTester = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [itemCount, setItemCount] = useState(4);
  const [responses, setResponses] = useState([2, 3, 1, 2]);
  const [customItems, setCustomItems] = useState('');
  const [useCustomItems, setUseCustomItems] = useState(false);

  const sampleItems = [
    { thresholds: [-2.5, -0.5, 1.5], slope: 1.2, label: 'Easy item, good discrimination' },
    { thresholds: [-1.5, 0.5, 2.0], slope: 1.5, label: 'Medium difficulty' },
    { thresholds: [-0.5, 1.0, 2.5], slope: 1.0, label: 'Harder item' },
    { thresholds: [0.0, 1.5, 3.0], slope: 1.8, label: 'Hardest item, high discrimination' },
  ];

  const handleResponseChange = (index, value) => {
    const newResponses = [...responses];
    newResponses[index] = parseInt(value) || 0;
    setResponses(newResponses);
  };

  const addItem = () => {
    setItemCount(prev => prev + 1);
    setResponses(prev => [...prev, 0]);
  };

  const removeItem = () => {
    if (itemCount > 1) {
      setItemCount(prev => prev - 1);
      setResponses(prev => prev.slice(0, -1));
    }
  };

  const runTest = async () => {
    setLoading(true);
    setResult(null);

    try {
      let items;
      if (useCustomItems && customItems) {
        try {
          items = JSON.parse(customItems);
        } catch (e) {
          toast.error('Invalid JSON for custom items');
          setLoading(false);
          return;
        }
      } else {
        items = sampleItems.slice(0, itemCount).map(item => [
          item.thresholds,
          [item.slope],
          [0]
        ]);
      }

      const response = await api.post('/api/dev/test-adaptest/', {
        items: items,
        responses: responses.slice(0, itemCount)
      });

      setResult(response.data);
      if (response.data.success) {
        toast.success('Adaptest API call successful!');
      } else {
        toast.error('Adaptest API call failed');
      }
    } catch (error) {
      console.error('Adaptest test error:', error);
      setResult({
        success: false,
        error: error.response?.data?.error || error.message
      });
      toast.error('Failed to call Adaptest API');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg dark:shadow-gray-900/20 p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-800">Adaptest IRT Scoring API Tester</h3>
          <p className="text-sm text-gray-500 mt-1">
            Test the external Adaptest service for IRT-based score calculations
          </p>
        </div>
        <div className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full">
          https://adaptest.azurewebsites.net/
        </div>
      </div>

      <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-700">IRT Item Parameters</h4>
          <label className="flex items-center text-sm">
            <input
              type="checkbox"
              checked={useCustomItems}
              onChange={(e) => setUseCustomItems(e.target.checked)}
              className="mr-2"
            />
            Use custom JSON
          </label>
        </div>

        {useCustomItems ? (
          <textarea
            value={customItems}
            onChange={(e) => setCustomItems(e.target.value)}
            placeholder='[[[-2.5, -0.5, 1.5], [1.2], [0]], ...]'
            className="w-full h-32 px-3 py-2 border rounded-md font-mono text-sm"
          />
        ) : (
          <div className="space-y-2">
            {sampleItems.slice(0, itemCount).map((item, idx) => (
              <div key={idx} className="flex items-center text-sm bg-white dark:bg-gray-800 p-2 rounded border">
                <span className="font-medium text-gray-600 w-16">Item {idx + 1}:</span>
                <span className="text-gray-500 flex-1">
                  thresholds={JSON.stringify(item.thresholds)}, slope={item.slope}
                </span>
                <span className="text-xs text-gray-400 italic">{item.label}</span>
              </div>
            ))}
            <div className="flex space-x-2 mt-2">
              <button
                onClick={removeItem}
                disabled={itemCount <= 1}
                className="px-3 py-1 text-sm bg-red-100 text-red-600 rounded hover:bg-red-200 disabled:opacity-50"
              >
                - Remove
              </button>
              <button
                onClick={addItem}
                disabled={itemCount >= sampleItems.length}
                className="px-3 py-1 text-sm bg-green-100 text-green-600 rounded hover:bg-green-200 disabled:opacity-50"
              >
                + Add Item
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
        <h4 className="font-semibold text-gray-700 mb-3">Participant Responses (0-indexed)</h4>
        <div className="grid grid-cols-4 gap-3">
          {responses.slice(0, itemCount).map((resp, idx) => (
            <div key={idx} className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Item {idx + 1}:</label>
              <input
                type="number"
                min="0"
                max="3"
                value={resp}
                onChange={(e) => handleResponseChange(idx, e.target.value)}
                className="w-16 px-2 py-1 border rounded text-center"
              />
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          For 4-category items: 0=lowest, 1=second, 2=third, 3=highest
        </p>
      </div>

      <div className="flex justify-end">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={runTest}
          disabled={loading}
          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg font-medium shadow-md dark:shadow-gray-900/20 hover:shadow-lg dark:hover:shadow-gray-900/20 disabled:opacity-50"
        >
          {loading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Calling Adaptest...
            </>
          ) : (
            <>
              <Activity className="w-5 h-5 mr-2" />
              Run IRT Scoring Test
            </>
          )}
        </motion.button>
      </div>

      {result && (
        <div className={`rounded-lg p-4 ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center mb-3">
            {result.success ? (
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500 mr-2" />
            )}
            <span className={`font-semibold ${result.success ? 'text-green-800' : 'text-red-800'}`}>
              {result.success ? 'API Call Successful' : 'API Call Failed'}
            </span>
          </div>

          {result.success ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border text-center">
                  <div className="text-3xl font-bold text-primary-600">{result.eap?.toFixed(4)}</div>
                  <div className="text-sm text-gray-500">EAP (Theta)</div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border text-center">
                  <div className="text-3xl font-bold text-blue-600">{result.sem?.toFixed(4)}</div>
                  <div className="text-sm text-gray-500">SEM</div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border text-center">
                  <div className="text-3xl font-bold text-purple-600">{result.t_score?.toFixed(2)}</div>
                  <div className="text-sm text-gray-500">T-Score (EAP×10+50)</div>
                </div>
              </div>

              {result.processing_log && result.processing_log.length > 0 && (
                <div className="bg-white dark:bg-gray-800 p-3 rounded border">
                  <h5 className="font-medium text-gray-700 mb-2">Processing Log</h5>
                  <div className="text-sm space-y-1 font-mono text-gray-600">
                    {result.processing_log.map((log, idx) => (
                      <div key={idx}>{log}</div>
                    ))}
                  </div>
                </div>
              )}

              <details className="bg-white dark:bg-gray-800 p-3 rounded border">
                <summary className="cursor-pointer font-medium text-gray-700">Raw API Response</summary>
                <pre className="mt-2 text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded overflow-auto max-h-40">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </details>
            </div>
          ) : (
            <div className="text-red-700">
              <p className="font-medium">Error:</p>
              <p>{result.error}</p>
            </div>
          )}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">About IRT Scoring</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li><strong>EAP (Expected A Posteriori):</strong> The estimated trait level (theta) on a standard normal scale (-3 to +3)</li>
          <li><strong>SEM (Standard Error of Measurement):</strong> Precision of the estimate (lower is better)</li>
          <li><strong>T-Score:</strong> Rescaled score using formula (EAP × 10) + 50, giving a mean of 50 and SD of 10</li>
          <li><strong>Thresholds:</strong> Difficulty parameters for each response category transition</li>
          <li><strong>Slope:</strong> Discrimination parameter (higher = item better differentiates trait levels)</li>
        </ul>
      </div>
    </div>
  );
};

export default AdaptestTester;
